import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Megaphone, Plus, ArrowLeft, Upload, X,
  Clock, CheckCircle, XCircle, Eye, Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PartnerShowcasesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    link_url: "",
    format: "horizontal",
    image_url: ""
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/showcases?partner_id=${user.id}`);
      setItems(response.data);
    } catch (error) {
      console.error("Error fetching showcases:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    uploadFormData.append("format", formData.format);

    try {
      const response = await axios.post(`${API}/showcases/upload-image`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setFormData({ ...formData, image_url: response.data.image_url });
      toast.success("Image uploadée");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.image_url) {
      toast.error("Titre et image requis");
      return;
    }

    const submitFormData = new FormData();
    submitFormData.append("title", formData.title);
    submitFormData.append("description", formData.description || "");
    submitFormData.append("link_url", formData.link_url || "");
    submitFormData.append("format", formData.format);
    submitFormData.append("image_url", formData.image_url);
    submitFormData.append("partner_id", user.id);
    submitFormData.append("created_by", user.id);

    try {
      await axios.post(`${API}/showcases`, submitFormData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Publicité soumise pour validation par l'administrateur");
      setShowCreateForm(false);
      setFormData({ title: "", description: "", link_url: "", format: "horizontal", image_url: "" });
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de création");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Supprimer cette publicité ?")) return;
    try {
      await axios.delete(`${API}/showcases/${itemId}`);
      toast.success("Publicité supprimée");
      fetchItems();
    } catch (error) {
      toast.error("Erreur de suppression");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: "bg-yellow-100 text-yellow-800", icon: Clock, label: "En attente de validation" },
      approved: { bg: "bg-green-100 text-green-800", icon: CheckCircle, label: "Approuvée" },
      rejected: { bg: "bg-red-100 text-red-800", icon: XCircle, label: "Rejetée" }
    };
    return badges[status] || badges.pending;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <nav className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 md:px-8 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/partner')}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
                <Megaphone className="w-5 h-5 inline mr-2" />
                Mes Publicités
              </h1>
              <p className="text-xs text-purple-100">{user.business_name || user.full_name}</p>
            </div>
          </div>
          <Button onClick={onLogout} variant="outline" className="rounded-full text-white border-white hover:bg-white hover:text-purple-700">
            Déconnexion
          </Button>
        </div>
      </nav>

      <div className="px-4 md:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Info Card */}
          <Card className="mb-6 bg-purple-50 border-purple-200">
            <CardContent className="pt-6">
              <h3 className="font-bold text-purple-800 mb-2">Promouvoir votre boutique</h3>
              <p className="text-sm text-purple-700">
                Créez une publicité pour apparaître sur la page d'accueil d'ARTEKNOS.
                Vos publicités seront validées par l'administrateur avant d'être diffusées.
              </p>
            </CardContent>
          </Card>

          {/* Create Button */}
          <div className="flex justify-end mb-6">
            <Button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="rounded-full bg-purple-600 hover:bg-purple-700"
              data-testid="new-showcase-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle publicité
            </Button>
          </div>

          {/* Create Form */}
          {showCreateForm && (
            <Card className="mb-6 border-purple-200">
              <CardHeader className="bg-purple-50">
                <CardTitle className="flex items-center gap-2 text-purple-800">
                  <Plus className="w-5 h-5" />
                  Créer une publicité
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleCreateItem} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Titre *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Ex: Soldes d'hiver -50%"
                        required
                        data-testid="showcase-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="format">Format d'image</Label>
                      <select
                        id="format"
                        value={formData.format}
                        onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                        className="w-full h-10 rounded-md border border-input bg-background px-3"
                        data-testid="showcase-format-select"
                      >
                        <option value="horizontal">Bannière horizontale (1200x400)</option>
                        <option value="square">Carré (600x600)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Description courte de votre promotion"
                      data-testid="showcase-description-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="link_url">Lien vers votre boutique (optionnel)</Label>
                    <Input
                      id="link_url"
                      type="url"
                      value={formData.link_url}
                      onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                      placeholder="https://..."
                      data-testid="showcase-link-input"
                    />
                  </div>

                  <div>
                    <Label>Image de la publicité *</Label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <div className="flex gap-4 items-start mt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                        className="flex-shrink-0"
                        data-testid="showcase-upload-btn"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingImage ? "Upload..." : "Choisir image"}
                      </Button>
                      {formData.image_url && (
                        <div className="relative">
                          <img
                            src={BACKEND_URL + formData.image_url}
                            alt="Preview"
                            className="h-24 rounded-lg object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, image_url: "" })}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Formats: JPG, PNG, WebP, GIF (max 10MB pour GIF) - Taille recommandée: {formData.format === "horizontal" ? "1200x400" : "600x600"} pixels
                    </p>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="showcase-submit-btn">
                      Soumettre pour validation
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                      Annuler
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Items List */}
          <Card>
            <CardHeader>
              <CardTitle>Mes publicités ({items.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">Chargement...</div>
              ) : items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Vous n'avez pas encore de publicité.</p>
                  <p className="text-sm">Créez-en une pour promouvoir votre boutique!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => {
                    const statusBadge = getStatusBadge(item.status);
                    const StatusIcon = statusBadge.icon;
                    return (
                      <div
                        key={item.id}
                        className="border rounded-xl p-4 flex items-center gap-4 hover:bg-gray-50"
                        data-testid={`showcase-item-${item.id}`}
                      >
                        {item.image_url && (
                          <img
                            src={BACKEND_URL + item.image_url}
                            alt={item.title}
                            className="w-32 h-20 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{item.title}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${statusBadge.bg}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusBadge.label}
                            </span>
                          </div>
                          {item.description && (
                            <p className="text-sm text-gray-500">{item.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-gray-400 mt-1">
                            <span>{(item.format === "banner" || item.format === "horizontal") ? "Bannière" : "Carré"}</span>
                            {item.status === "approved" && (
                              <>
                                <span>{item.views || 0} vues</span>
                                <span>{item.clicks || 0} clics</span>
                              </>
                            )}
                          </div>
                          {item.status === "rejected" && item.rejection_reason && (
                            <p className="text-xs text-red-600 mt-1">
                              Raison: {item.rejection_reason}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {item.status === "approved" && item.is_active && (
                            <span className="text-xs px-2 py-1 bg-green-500 text-white rounded-full flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              En ligne
                            </span>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 rounded-full"
                            onClick={() => handleDeleteItem(item.id)}
                            data-testid={`delete-showcase-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
